import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import {
  PettyCash,
  PettyCashItem,
  PettyCashItemStatus,
} from '../../dto/PettyCash';
import { useParams } from 'react-router-dom';
import { useConnection } from '../../connection/connection';
import { useBusinessContext } from '../../context/BusinessContext';
import PettyCashItemEditModal, {
  usePettyCashItemEditModal,
} from './PettyCashItemEditModal';
import { StatusTag } from '../../common/StatusTag';
import { Utils } from '../../common/Utils';
import { Button, Input, Modal, Select, Table } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { useEventListener } from '../../common/EventContext';
import { WebUxTitle } from '../WebUxContext';

type SelectPettyCashItem = {
  pettyCashId: number;
  name: string;
  siteId: string;
};

type Props = {};

export const MyPettyCashItemPage: FC<Props> = (props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [items, setItems] = useState<PettyCashItem[]>([]);
  const connection = useConnection();
  const business = useBusinessContext();
  const [search, setSearch] = useState<string>();
  const [siteId, setSiteId] = useState<number>(0);
  const modal = usePettyCashItemEditModal();
  const [pettyCashId, setPettyCashId] = useState<number>(0);
  const [pettyCash, setPettyCash] = useState<SelectPettyCashItem[]>([]);
  const params = useParams<{
    pettyCashId: string;
  }>();
  const columns: any = useMemo(
    () => [
      {
        key: 'name',
        title: 'Name',
        dataIndex: 'name',
      },
      {
        key: 'notes',
        title: 'Notes',
        dataIndex: 'notes',
      },
      {
        key: 'site',
        title: 'Site',
        dataIndex: 'siteId',
        render: (r: string, record: any) => business.sites[r].name,
      },
      {
        key: 'status',
        title: 'Status',
        dataIndex: 'status',
        render: (text: string) => (
          <StatusTag
            options={{ Approved: 'green', Declined: 'red', Pending: 'orange' }}
            value={text}
          />
        ),
      },
      {
        key: 'amount',
        title: 'Amount',
        dataIndex: 'amount',
        render: (text: number) => Utils.formatPrice(text ?? 0),
      },
      {
        key: 'actions',
        title: '',
        dataIndex: 'id',
        render: (text: string, record: any) => (
          <>
            {record.status === PettyCashItemStatus.Pending && (
              <Button
                type="link"
                onClick={() =>
                  modal.current?.edit(
                    record.pettyCashId,
                    record.pettyCashItemId,
                  )
                }
              >
                <EditOutlined />
              </Button>
            )}
          </>
        ),
      },
    ],
    [business.sites, modal],
  );
  useEffect(() => {
    setLoading(true);
    connection
      .get<{
        pettyCash: SelectPettyCashItem[];
        pettyCashItems: PettyCashItem[];
      }>(`${business.businessId}/0/petty-cash-item/own`)
      .then((res) => {
        setItems(res.data.pettyCashItems);
        setPettyCashId(res.data.pettyCash?.[0]?.pettyCashId);
        setPettyCash(res.data.pettyCash);
      })
      .finally(() => setLoading(false));
  }, [business.businessId, connection, params.pettyCashId]);

  useEventListener(
    'MYPETTYCASHITEM',
    useCallback((dt) => {
      const item: PettyCashItem = dt.data;
      if (dt.action === 'CREATE') {
        setItems((prevState) => [item, ...prevState]);
      } else if (dt.action === 'UPDATE') {
        setItems((prevState) =>
          prevState.map((a) =>
            a.pettyCashItemId === item.pettyCashItemId ? item : a,
          ),
        );
      }
    }, []),
  );

  const warehouses = useMemo(() => {
    const s = (search ?? '').trim().toLowerCase();
    const b = siteId === 0 ? undefined : siteId;
    return items
      .map((e) => ({ ...e, key: e.pettyCashItemId }))
      .filter(
        (item) =>
          !search ||
          search.trim() === '' ||
          item.name.toLowerCase().includes(s),
      )
      .filter((item) => !b || b === item.siteId);
  }, [items, search, siteId]);

  return (
    <div className="flex flex-col gap-2">
      <WebUxTitle>My Petty Cash Account</WebUxTitle>
      <div className="flex gap-1 justify-between">
        <div className="flex gap-1">
          {!!pettyCash && pettyCash.length > 0 && (
            <Select
              value={pettyCashId}
              onChange={setPettyCashId}
              options={[
                ...pettyCash.map((e) => ({
                  value: e.pettyCashId,
                  label: e.name,
                })),
              ]}
              className="w-[150px]"
            />
          )}
          <div className="w-[500px]">
            <Input.Search
              onSearch={setSearch}
              allowClear={true}
              className="w-full"
              placeholder="Search Petty cash by name"
            />
          </div>
        </div>
        <div className="flex gap-1 justify-between">
          {pettyCashId && (
            <Button
              type="primary"
              onClick={() => modal.current?.create(+pettyCashId)}
            >
              Create Item
            </Button>
          )}
        </div>
      </div>
      <Table
        size="small"
        loading={loading}
        pagination={false}
        columns={columns}
        dataSource={warehouses}
      />
      <PettyCashItemEditModal ref={modal} user={true} />
    </div>
  );
};
